import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart2,
  ChevronRight,
  RefreshCw,
  Search,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import {
  Badge,
  EmptyState,
  PageHeader,
  StatCard,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../ui';
import { portfolioService } from '../../services/portfolio.service';
import { customersService } from '../../services/customers.service';
import {
  PORTFOLIO_DISPLAY_CURRENCIES,
  listSnapshot,
  portfolioIdCandidates,
  portfolioRowKey,
  resolveCustomerPortfolio,
} from './portfolioModel';

/* ── helpers ────────────────────────────────────────────────────────────────── */
const fmtNum = (n, d = 2) =>
  n != null
    ? Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
    : '—';

const fmtMoney = (n, currency = 'AED') => {
  if (n == null) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}${currency ? `${currency} ` : ''}${fmtNum(Math.abs(n))}`;
};

const fmtSigned = (n) => {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';
  return `${sign}${fmtNum(Math.abs(n))}`;
};

const fmtPct = (n) => {
  if (n == null || n === '') return null;
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}%`;
};

const avatarInitials = (first, last, email) => {
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  if (first) return first[0].toUpperCase();
  if (email) return email[0].toUpperCase();
  return '?';
};

const PAGE_SIZE = 20;
// Bounded pagination — fetches up to 1,000 customers (10 pages x 100); most
// partner orgs are well below this ceiling.
const MAX_CUSTOMER_PAGES = 10;

/* ── KPI strip ──────────────────────────────────────────────────────────────── */
const KpiStrip = ({ stats, loading, unavailable, currency, caption }) => {
  const unrealizedPnl = stats?.unrealizedPnl ?? 0;
  const pnlPos = unrealizedPnl >= 0;
  const hidden = loading || unavailable;

  return (
    <div className="mb-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total value"
          value={hidden ? '—' : fmtMoney(stats.totalSum, currency)}
          icon={Wallet}
          trend="neutral"
        />
        <StatCard
          label="Invested"
          value={hidden ? '—' : fmtMoney(stats.marketValue, currency)}
          icon={BarChart2}
          trend="neutral"
        />
        <StatCard
          label="Cash"
          value={hidden ? '—' : fmtMoney(stats.cash, currency)}
          icon={Wallet}
          trend="neutral"
        />
        <StatCard
          label="Unrealized P&L"
          value={hidden ? '—' : `${pnlPos && unrealizedPnl !== 0 ? '+' : ''}${fmtMoney(unrealizedPnl, currency)}`}
          icon={pnlPos ? TrendingUp : TrendingDown}
          trend={hidden ? 'neutral' : pnlPos ? 'up' : 'down'}
        />
      </div>
      {caption && <p className="mt-2 text-xs text-ink-400">{caption}</p>}
    </div>
  );
};

/* ── Portfolio row status ───────────────────────────────────────────────────── */
const PortfolioStatusBadge = ({ marketValue, cash, positionCount, error }) => {
  if (error) return <Badge tone="danger" dot>Unavailable</Badge>;
  if (positionCount > 0 && marketValue > 0) return <Badge tone="success" dot>Invested</Badge>;
  if (positionCount > 0) return <Badge tone="info" dot>Positions</Badge>;
  if (cash > 0) return <Badge tone="info" dot>Cash</Badge>;
  return <Badge tone="neutral" dot>Empty</Badge>;
};

/* ── PnL cell ───────────────────────────────────────────────────────────────── */
const PnLCell = ({ value, pct }) => {
  const num = parseFloat(value ?? '0');
  const pos = num >= 0;
  const pctLabel = fmtPct(pct);
  if (num === 0 && !pctLabel) return <span className="font-mono text-sm tnum text-ink-400">—</span>;
  return (
    <div className={`flex flex-col items-end ${pos ? 'text-success-700' : 'text-danger-700'}`}>
      <div className="flex items-center gap-1">
        {num === 0 ? null : pos ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        <span className="font-mono text-sm tnum">{fmtSigned(num)}</span>
      </div>
      {pctLabel && <span className="text-xs font-mono tnum opacity-70">{pctLabel}</span>}
    </div>
  );
};

/* ── Row loading skeleton ───────────────────────────────────────────────────── */
const RowSkeleton = () => (
  <TR>
    <TD><div className="h-3 bg-ink-100 animate-pulse rounded w-32" /></TD>
    <TD className="text-right"><div className="h-3 bg-ink-100 animate-pulse rounded w-24 ml-auto" /></TD>
    <TD className="text-right"><div className="h-3 bg-ink-100 animate-pulse rounded w-20 ml-auto" /></TD>
    <TD className="text-right"><div className="h-3 bg-ink-100 animate-pulse rounded w-16 ml-auto" /></TD>
    <TD className="text-right"><div className="h-3 bg-ink-100 animate-pulse rounded w-16 ml-auto" /></TD>
    <TD className="text-right"><div className="h-3 bg-ink-100 animate-pulse rounded w-8 ml-auto" /></TD>
    <TD><div className="h-4 bg-ink-100 animate-pulse rounded w-16" /></TD>
    <TD />
  </TR>
);

/* ── fetch portfolio for one customer ───────────────────────────────────────── */
const emptySnapshot = (rowKey, displayCurrency, errorMessage) => ({
  rowKey,
  customerId: '',
  currency: displayCurrency,
  totalSum: 0,
  marketValue: 0,
  cash: 0,
  unrealizedPnl: 0,
  unrealizedPct: null,
  positionCount: 0,
  error: true,
  errorMessage: errorMessage || 'Portfolio unavailable',
});

const fetchCustomerPortfolio = async (customer, displayCurrency) => {
  const rowKey = portfolioRowKey(customer);
  try {
    const { customerId, portfolio } = await resolveCustomerPortfolio(
      (id, options) => portfolioService.getCustomerPortfolio(id, options),
      customer,
      { displayCurrency },
    );
    return {
      rowKey,
      customerId,
      ...listSnapshot(portfolio, displayCurrency),
    };
  } catch (err) {
    return emptySnapshot(
      rowKey,
      displayCurrency,
      err?.response?.data?.message || err?.message,
    );
  }
};

/* ── component ──────────────────────────────────────────────────────────────── */
const PortfolioListPage = () => {
  const navigate = useNavigate();

  const [displayCurrency, setDisplayCurrency] = useState('AED');

  const [allCustomers, setAllCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [customersError, setCustomersError] = useState(null);

  const [portfolioData, setPortfolioData] = useState({}); // keyed by customerId
  const [loadingPortfolioIds, setLoadingPortfolioIds] = useState(new Set());

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const abortRef = useRef(null);

  /* load all customers once, paging via the cursor the gateway actually
     supports (it has no offset param — the /v1/users endpoint is cursor-only) */
  const loadCustomers = useCallback(async () => {
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      let combined = [];
      let cursor;
      for (let page = 0; page < MAX_CUSTOMER_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await customersService.listCustomers({ limit: 100, cursor });
        const pageUsers = Array.isArray(result?.users) ? result.users : [];
        combined = combined.concat(pageUsers);
        cursor = result?.nextCursor || null;
        if (!cursor) break;
      }
      setAllCustomers(combined);
    } catch (err) {
      setCustomersError(err?.response?.data?.message || 'Failed to load customers.');
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  /* load portfolio data for visible page of customers */
  useEffect(() => {
    if (customersLoading || !allCustomers.length) return;

    const filtered = allCustomers.filter((c) => {
      const q = search.toLowerCase();
      const haystack = `${c.firstName || ''} ${c.lastName || ''} ${c.email || ''}`.toLowerCase();
      return !q || haystack.includes(q);
    });

    const pageRows = filtered
      .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
      .filter((customer) => {
        const key = portfolioRowKey(customer);
        return key && !portfolioData[key];
      });

    if (!pageRows.length) return;

    abortRef.current?.();
    let cancelled = false;
    abortRef.current = () => { cancelled = true; };

    const rowKeys = pageRows.map((customer) => portfolioRowKey(customer));
    setLoadingPortfolioIds((prev) => {
      const next = new Set(prev);
      rowKeys.forEach((id) => next.add(id));
      return next;
    });

    Promise.all(pageRows.map((customer) => fetchCustomerPortfolio(customer, displayCurrency))).then((results) => {
      if (cancelled) return;
      setPortfolioData((prev) => {
        const next = { ...prev };
        results.forEach((r) => { next[r.rowKey] = r; });
        return next;
      });
      setLoadingPortfolioIds((prev) => {
        const next = new Set(prev);
        rowKeys.forEach((id) => next.delete(id));
        return next;
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCustomers, customersLoading, page, search, displayCurrency]);

  /* derived */
  const filtered = allCustomers.filter((c) => {
    const q = search.toLowerCase();
    const haystack = `${c.firstName || ''} ${c.lastName || ''} ${c.email || ''}`.toLowerCase();
    return !q || haystack.includes(q);
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageCustomers = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleCurrency = (e) => {
    setDisplayCurrency(e.target.value);
    setPortfolioData({});
    setPage(1);
  };

  const handleRefresh = () => {
    setPortfolioData({});
    loadCustomers();
  };

  const pageStats = pageCustomers.reduce(
    (acc, customer) => {
      const pd = portfolioData[portfolioRowKey(customer)];
      if (!pd || pd.error) return acc;
      acc.count += 1;
      acc.totalSum += pd.totalSum;
      acc.marketValue += pd.marketValue;
      acc.cash += pd.cash;
      acc.unrealizedPnl += pd.unrealizedPnl;
      return acc;
    },
    { count: 0, totalSum: 0, marketValue: 0, cash: 0, unrealizedPnl: 0 },
  );
  const pagePending = pageCustomers.some((customer) => {
    const id = portfolioRowKey(customer);
    return !id || loadingPortfolioIds.has(id) || !portfolioData[id];
  });
  const pageHasError = pageCustomers.some((customer) => portfolioData[portfolioRowKey(customer)]?.error);
  const kpiLoading = customersLoading || (pageCustomers.length > 0 && pageStats.count === 0 && pagePending);
  const kpiUnavailable = !kpiLoading && pageCustomers.length > 0 && pageStats.count === 0 && pageHasError;
  const loadedCaption = pageCustomers.length ? ` · ${pageStats.count} of ${pageCustomers.length} loaded` : '';
  const kpiCaption = customersLoading
    ? null
    : kpiUnavailable
      ? `Portfolio unavailable for customers on this page${loadedCaption}.`
      : `Totals for customers on this page, in ${displayCurrency}${loadedCaption}.`;

  return (
    <div>
      <PageHeader
        title="Portfolio"
        description="Positions, cash balances, and valuation for each customer."
        actions={
          <>
            <label className="flex items-center gap-2 text-xs text-ink-500">
              Display currency
              <select
                aria-label="Display currency"
                value={displayCurrency}
                onChange={handleCurrency}
                className="input h-8 text-sm w-28"
              >
                {PORTFOLIO_DISPLAY_CURRENCIES.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </label>
            <button type="button" onClick={handleRefresh} className="btn btn-sm btn-secondary">
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </>
        }
      />

      <KpiStrip
        stats={pageStats}
        loading={kpiLoading}
        unavailable={kpiUnavailable}
        currency={displayCurrency}
        caption={kpiCaption}
      />

      {/* Table card */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        {/* Toolbar */}
        <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-400" />
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Search customers…"
              className="input pl-8 text-sm h-8"
            />
          </div>
          <p className="text-xs text-ink-400 ml-auto tnum">
            {customersLoading ? '…' : `${filtered.length} customers`}
          </p>
        </div>

        {/* Table */}
        {customersError ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center px-6">
            <p className="text-sm text-danger-600">{customersError}</p>
            <button type="button" onClick={loadCustomers} className="btn btn-sm btn-secondary">
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        ) : customersLoading ? (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <THead>
                <TR>
                  <TH>Customer</TH>
                  <TH className="text-right">Total ({displayCurrency})</TH>
                  <TH className="text-right">Invested</TH>
                  <TH className="text-right">Cash</TH>
                  <TH className="text-right">Unrealized P&amp;L</TH>
                  <TH className="text-right">Assets</TH>
                  <TH>Status</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>{Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)}</TBody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Users} title="No customers found" description={search ? 'Try a different search term.' : 'No customers in this organisation yet.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="table w-full">
              <THead>
                <TR>
                  <TH>Customer</TH>
                  <TH className="text-right w-36">Total ({displayCurrency})</TH>
                  <TH className="text-right w-32">Invested</TH>
                  <TH className="text-right w-32">Cash</TH>
                  <TH className="text-right w-40">Unrealized P&amp;L</TH>
                  <TH className="text-right w-20">Assets</TH>
                  <TH className="w-28">Status</TH>
                  <TH className="w-8" />
                </TR>
              </THead>
              <TBody>
                {pageCustomers.map((customer) => {
                  const id = portfolioRowKey(customer);
                  const pd = portfolioData[id];
                  const loading = loadingPortfolioIds.has(id);
                  const openId = (pd && !pd.error && pd.customerId) || portfolioIdCandidates(customer)[0] || '';
                  const initials = avatarInitials(customer.firstName, customer.lastName, customer.email);
                  const figuresReady = Boolean(pd && !pd.error && !loading);

                  return (
                    <TR
                      key={id || customer.email}
                      className="cursor-pointer hover:bg-accent-50/40 transition-colors"
                      onClick={() => openId && navigate(`/portfolio/${openId}?displayCurrency=${displayCurrency}`)}
                    >
                      <TD>
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-bold shrink-0">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink-900 truncate">
                              {customer.firstName} {customer.lastName}
                            </p>
                            <p className="text-xs text-ink-400 font-mono truncate">
                              {customer.email || customer.customerId}
                            </p>
                          </div>
                        </div>
                      </TD>
                      <TD className="text-right">
                        {loading ? (
                          <div className="h-3 bg-ink-100 animate-pulse rounded w-24 ml-auto" />
                        ) : figuresReady ? (
                          <span className="font-mono text-sm tnum text-ink-900">
                            {fmtNum(pd.totalSum)}
                            {pd.currency && pd.currency !== displayCurrency ? ` ${pd.currency}` : ''}
                          </span>
                        ) : (
                          <span className="text-ink-400 text-sm">—</span>
                        )}
                      </TD>
                      <TD className="text-right">
                        {loading ? (
                          <div className="h-3 bg-ink-100 animate-pulse rounded w-20 ml-auto" />
                        ) : figuresReady ? (
                          <span className="font-mono text-sm tnum text-ink-700">{fmtNum(pd.marketValue)}</span>
                        ) : (
                          <span className="text-ink-400 text-sm">—</span>
                        )}
                      </TD>
                      <TD className="text-right">
                        {loading ? (
                          <div className="h-3 bg-ink-100 animate-pulse rounded w-20 ml-auto" />
                        ) : figuresReady ? (
                          <span className="font-mono text-sm tnum text-ink-700">{fmtNum(pd.cash)}</span>
                        ) : (
                          <span className="text-ink-400 text-sm">—</span>
                        )}
                      </TD>
                      <TD className="text-right">
                        {loading ? (
                          <div className="h-3 bg-ink-100 animate-pulse rounded w-20 ml-auto" />
                        ) : figuresReady ? (
                          <PnLCell value={pd.unrealizedPnl} pct={pd.unrealizedPct} />
                        ) : (
                          <span className="text-ink-400 text-sm">—</span>
                        )}
                      </TD>
                      <TD className="text-right">
                        {loading ? (
                          <div className="h-3 bg-ink-100 animate-pulse rounded w-8 ml-auto" />
                        ) : figuresReady ? (
                          <span className="font-mono text-sm tnum text-ink-700">{pd.positionCount}</span>
                        ) : (
                          <span className="text-ink-400 text-sm">—</span>
                        )}
                      </TD>
                      <TD>
                        {pd && !loading ? (
                          <span title={pd.error ? pd.errorMessage : undefined}>
                            <PortfolioStatusBadge
                              marketValue={pd.marketValue}
                              cash={pd.cash}
                              positionCount={pd.positionCount}
                              error={pd.error}
                            />
                          </span>
                        ) : loading ? (
                          <div className="h-4 bg-ink-100 animate-pulse rounded w-16" />
                        ) : null}
                      </TD>
                      <TD>
                        <ChevronRight className="h-4 w-4 text-ink-300" />
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!customersLoading && !customersError && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100">
            <p className="text-xs text-ink-400 tnum">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn btn-sm btn-secondary"
              >
                Previous
              </button>
              <span className="text-xs text-ink-500 px-2 tnum">{page} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn btn-sm btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioListPage;
